///////////////////////////////////////////////////////////////////////////////////////
//                          Copyright (c) 2021 - 2023
//                           Coyote Logistics L.L.C.
//                         All Rights Reserved Worldwide
//
//WARNING:  This program (or document) is unpublished, proprietary
//property of Coyote Logistics L.L.C. and is to be maintained in strict confidence.
//Unauthorized reproduction, distribution or disclosure of this program
//(or document), or any program (or document) derived from it is
//prohibited by State and Federal law, and by local law outside of the U.S.
///////////////////////////////////////////////////////////////////////////////////////

import React, { useEffect, useState } from "react";
import {
  ScatterChart,
  Scatter,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Text,
  Brush
} from "recharts";

import { graphColors, division as divisionTypes } from "../../../helpers/types";
import messages from "../../../helpers/messages";

const CustomTooltip = ({ active, payload, xAxisUnit, division }) => {
  if (active && payload && payload.length) {
    return (
      <div className="tooltip card p-12 pb-8">
        <p>
          {messages.avgCost.defaultMessage}: {xAxisUnit}
          {payload[0].payload.averageCost}
        </p>
        <p>
          {division === divisionTypes.coy
            ? messages.avgCpm.defaultMessage
            : messages.avgCpKm.defaultMessage}
          : {xAxisUnit}
          {payload[0].payload.averageCpm}
        </p>
        <p>
          {payload[1].name}: {payload[1].value}
        </p>
      </div>
    );
  }

  return null;
};

export const FrequencyGraph = ({
  xAxisDataKey,
  xAxisName,
  data: unchangedData,
  currency,
  width = "100%",
  division,
  showBrush = false
}) => {
  const [data, setData] = useState(unchangedData);

  useEffect(() => {
    setData(unchangedData);
  }, [unchangedData]);

  const handleBrushChange = ({ startIndex, endIndex }) => {
    setData(unchangedData.slice(startIndex, endIndex));
  };

  return (
    <ResponsiveContainer className="frequency-graph" height={280} width={width}>
      <ScatterChart
        margin={{
          top: 55,
          right: 20,
          bottom: 10,
          left: 5
        }}
        data={unchangedData}
      >
        <CartesianGrid stroke="#EFF2FA" />
        <XAxis
          type="number"
          dataKey={xAxisDataKey}
          name={xAxisName}
          axisLine={false}
          tickLine={false}
          width={30}
          interval={0}
          textAnchor="end"
          label={{
            className: "x-axis-label",
            value: `${xAxisName} (${currency})`,
            position: "insideBottomRight",
            dy: -9
          }}
          height={30}
          dx={6}
          domain={["dataMin", "dataMax"]}
        />
        <YAxis
          type="number"
          dataKey="volume"
          name="Volume"
          axisLine={false}
          tickLine={false}
          label={
            <Text
              className="yaxis-label"
              x={-22}
              y={-15}
              dx={50}
              dy={40}
              offset={0}
              angle={0}
            >
              {messages.volume.defaultMessage}
            </Text>
          }
          width={40}
          allowDecimals={false}
          domain={["dataMin", "dataMax"]}
        />
        {showBrush && (
          <Brush
            dataKey={xAxisDataKey}
            stroke={graphColors.frequency}
            height={20}
            onChange={handleBrushChange}
          />
        )}
        <Scatter data={data} fill={graphColors.frequency} />
        <Tooltip
          content={<CustomTooltip xAxisUnit={currency} division={division} />}
        />
      </ScatterChart>
    </ResponsiveContainer>
  );
};

export default FrequencyGraph;
